<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] sm:flex items-center justify-between"
    >
      <div class="trezo-card-title flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <form class="relative sm:w-[265px]">
          <label
            class="leading-none absolute ltr:left-[13px] rtl:right-[13px] text-black dark:text-white mt-px top-1/2 -translate-y-1/2"
          >
            <i class="material-symbols-outlined !text-[20px]"> search </i>
          </label>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="directT('crm.customers.searchCustomer')"
            class="bg-gray-50 border border-gray-50 h-[36px] text-xs rounded-md w-full block text-black pt-[11px] pb-[12px] ltr:pl-[38px] rtl:pr-[38px] ltr:pr-[13px] ltr:md:pr-[16px] rtl:pl-[13px] rtl:md:pl-[16px] placeholder:text-gray-500 outline-0 dark:bg-[#15203c] dark:text-white dark:border-[#15203c] dark:placeholder:text-gray-400"
          />
        </form>
        <button
          type="button"
          @click="addNewCustomer"
          class="bg-primary-500 hover:bg-primary-600 text-white rounded-md px-4 py-2 text-sm font-medium transition-all flex items-center"
        >
          <i class="material-symbols-outlined !text-[18px] mr-1">add</i>
          {{ directT('crm.customers.addCustomer') }}
        </button>
      </div>

      <div class="trezo-card-subtitle mt-[15px] sm:mt-0">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block transition-all hover:text-primary-500': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px]"
            >
              {{ statusFilter === 'All' ? directT('crm.customers.all') : 
                 statusFilter === 'New' ? directT('crm.customers.new') : 
                 statusFilter === 'Active' ? directT('crm.customers.active') : 
                 statusFilter === 'Inactive' ? directT('crm.customers.inactive') : statusFilter }}
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>

          <ul
            v-if="isActive"
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[5] dark:bg-dark dark:shadow-none"
          >
            <li v-for="status in statusOptions" :key="status">
              <button
                type="button"
                @click="setStatusFilter(status)"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ status === 'All' ? directT('crm.customers.all') : 
                   status === 'New' ? directT('crm.customers.new') : 
                   status === 'Active' ? directT('crm.customers.active') : 
                   status === 'Inactive' ? directT('crm.customers.inactive') : status }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="trezo-card-content -mx-[20px] md:-mx-[25px]">
      <div v-if="loading" class="text-center p-5">
        <div class="spinner"></div>
        <p class="mt-2">{{ directT('crm.customers.loading') }}</p>
      </div>
      <div v-else-if="clients.length === 0" class="text-center p-5">
        <p>{{ directT('crm.customers.noCustomersFound') }}</p>
      </div>
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                <div class="form-check relative top-[2px]">
                  <input type="checkbox" class="cursor-pointer" @change="selectAllClients" v-model="selectAll" />
                </div>
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.id') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.customer') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.email') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.phone') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.source') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.receivedAmount') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.status') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('crm.customers.action') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="client in filteredClients" :key="client.id">
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="form-check relative top-[2px]">
                  <input 
                    type="checkbox" 
                    class="cursor-pointer" 
                    v-model="selectedClients" 
                    :value="client.id" 
                  />
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">
                  {{ client.id.substring(0, 8) }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="flex items-center">
                  <div class="ltr:ml-[12px] rtl:mr-[12px]">
                    <span class="block font-medium">
                      {{ client.first_name }} {{ client.last_name }}
                </span>
                </div>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                {{ client.email }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">
                  {{ client.phone }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">
                  {{ client.shopify_id ? 'Shopify' : client.woocommerce_id ? 'WooCommerce' : 'Manual' }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">
                  {{ formatCurrency(client.received) }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span
                  class="px-[8px] py-[3px] inline-block bg-primary-50 dark:bg-[#15203c] text-primary-500 rounded-sm font-medium text-xs"
                >
                  {{ client.status }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="flex items-center gap-[9px]">
                  <button
                    type="button"
                    class="text-primary-500 leading-none custom-tooltip"
                    id="customTooltip"
                    :data-text="directT('crm.customers.view')"
                    @click="viewCustomer(client)"
                  >
                    <i class="material-symbols-outlined !text-md">
                      visibility
                    </i>
                  </button>
                  <button
                    type="button"
                    class="text-gray-500 dark:text-gray-400 leading-none custom-tooltip"
                    id="customTooltip"
                    :data-text="directT('crm.customers.edit')"
                    @click="editCustomer(client)"
                  >
                    <i class="material-symbols-outlined !text-md"> edit </i>
                  </button>
                  <button
                    type="button"
                    class="text-danger-500 leading-none custom-tooltip"
                    id="customTooltip"
                    :data-text="directT('crm.customers.delete')"
                    @click="deleteClient(client.id)"
                  >
                    <i class="material-symbols-outlined !text-md"> delete </i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
                </div>

      <Pagination
        items="10"
        total="36"
        class="px-[20px] md:px-[25px] pt-[12px] md:pt-[14px]"
                    />
                  </div>
                  </div>

  <!-- Add New Customer Modal -->
  <div v-if="showAddModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="closeModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-2xl mx-4 z-10 relative max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">{{ directT('crm.customers.addNewCustomer') }}</h3>
        <button 
          @click="closeModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <form @submit.prevent="saveCustomer" class="space-y-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- First Name -->
          <div>
            <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.firstName') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="first_name"
              v-model="newCustomer.first_name"
              type="text"
              required
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterFirstName')"
            />
          </div>
          
          <!-- Last Name -->
          <div>
            <label for="last_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.lastName') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="last_name"
              v-model="newCustomer.last_name"
              type="text"
              required
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterLastName')"
            />
          </div>
          
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.email') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              v-model="newCustomer.email"
              type="email"
              required
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterEmail')"
            />
          </div>
          
          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.phone') }}
            </label>
            <input
              id="phone"
              v-model="newCustomer.phone"
              type="tel"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterPhone')"
            />
          </div>
          
          <!-- Status -->
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.status') }}
            </label>
            <select
              id="status"
              v-model="newCustomer.status"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
            >
              <option value="Active">{{ directT('crm.customers.active') }}</option>
              <option value="Inactive">{{ directT('crm.customers.inactive') }}</option>
              <option value="Pending">{{ directT('crm.customers.pending') }}</option>
            </select>
          </div>
          
          <!-- Received -->
          <div>
            <label for="received" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.receivedAmount') }}
            </label>
            <input
              id="received"
              v-model="newCustomer.received"
              type="number"
              min="0"
              step="0.01"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterAmount')"
            />
          </div>
          
          <!-- Address -->
          <div class="sm:col-span-2">
            <label for="address" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.address') }}
            </label>
            <input
              id="address"
              v-model="newCustomer.address"
              type="text"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterAddress')"
            />
          </div>
          
          <!-- City -->
          <div>
            <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.city') }}
            </label>
            <input
              id="city"
              v-model="newCustomer.city"
              type="text"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterCity')"
            />
          </div>
          
          <!-- State -->
          <div>
            <label for="state" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.state') }}
            </label>
            <input
              id="state"
              v-model="newCustomer.state"
              type="text"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterState')"
            />
          </div>
          
          <!-- Country -->
          <div>
            <label for="country" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {{ directT('crm.customers.country') }}
            </label>
            <input
              id="country"
              v-model="newCustomer.country"
              type="text"
              class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-[#132034] dark:border-[#132034] dark:text-white"
              :placeholder="directT('crm.customers.enterCountry')"
            />
          </div>
        </div>
        
        <div class="flex justify-end space-x-3 mt-6">
          <button 
            type="button" 
            @click="closeModal"
            class="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
          >
            {{ directT('crm.customers.cancel') }}
          </button>
          <button 
            type="submit" 
            :disabled="loading"
            class="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all flex items-center justify-center min-w-[100px]"
          >
            <span v-if="loading" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            {{ loading ? directT('crm.customers.saving') : directT('crm.customers.save') }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="cancelDelete"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">{{ directT('crm.customers.confirmDeletion') }}</h3>
        <button 
          @click="cancelDelete" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <div class="mb-6">
        <p class="text-gray-700 dark:text-gray-300">{{ directT('crm.customers.deleteConfirmation') }}</p>
      </div>
      
      <div class="flex flex-col sm:flex-row sm:justify-end gap-2">
        <button 
          type="button" 
          @click="cancelDelete"
          class="w-full sm:w-auto px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746] order-2 sm:order-1"
        >
          {{ directT('crm.customers.cancel') }}
        </button>
        <button 
          type="button" 
          @click="confirmDelete"
          class="w-full sm:w-auto px-4 py-2.5 bg-danger-500 hover:bg-danger-600 text-white rounded-md transition-all order-1 sm:order-2 mb-2 sm:mb-0"
          :disabled="deleting"
        >
          {{ deleting ? directT('crm.customers.deleting') : directT('crm.customers.deleteCustomer') }}
        </button>
      </div>
    </div>
  </div>

  <!-- View Customer Modal -->
  <div v-if="showViewModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="closeViewModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">{{ directT('crm.customers.customerDetails') }}</h3>
        <button 
          @click="closeViewModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Customer Name -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.fullName') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.first_name }} {{ selectedCustomer?.last_name }}</p>
          </div>
          
          <!-- Email -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.email') }}</p>
            <p class="text-base text-black dark:text-white break-words">{{ selectedCustomer?.email || 'N/A' }}</p>
          </div>
          
          <!-- Phone -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.phone') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.phone || 'N/A' }}</p>
          </div>
          
          <!-- Status -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.status') }}</p>
            <p class="text-base">
              <span class="px-[8px] py-[3px] inline-block bg-primary-50 dark:bg-[#15203c] text-primary-500 rounded-sm font-medium text-xs">
                {{ selectedCustomer?.status }}
              </span>
            </p>
          </div>
          
          <!-- Address -->
          <div class="md:col-span-2">
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.address') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.address || 'N/A' }}</p>
          </div>
          
          <!-- City -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.city') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.city || 'N/A' }}</p>
          </div>
          
          <!-- State -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.state') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.state || 'N/A' }}</p>
          </div>
          
          <!-- Country -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.country') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.country || 'N/A' }}</p>
          </div>
          
          <!-- Received Amount -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.receivedAmount') }}</p>
            <p class="text-base text-black dark:text-white">{{ formatCurrency(selectedCustomer?.received) }}</p>
          </div>
          
          <!-- Source -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.source') }}</p>
            <p class="text-base text-black dark:text-white">
              {{ selectedCustomer?.shopify_id ? directT('crm.customers.shopify') : 
                 selectedCustomer?.woocommerce_id ? directT('crm.customers.wooCommerce') : 
                 directT('crm.customers.manual') }}
            </p>
          </div>
          
          <!-- ID -->
          <div>
            <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ directT('crm.customers.id') }}</p>
            <p class="text-base text-black dark:text-white">{{ selectedCustomer?.id || 'N/A' }}</p>
          </div>
        </div>
      </div>
      
      <div class="flex justify-end mt-6">
        <button 
          type="button" 
          @click="closeViewModal"
          class="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
        >
          {{ directT('crm.customers.close') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Edit Customer Modal -->
  <div v-if="showEditModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="cancelEdit"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">{{ directT('crm.customers.editCustomer') }}</h3>
        <button 
          @click="cancelEdit" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <form @submit.prevent="saveEdit" class="pb-1">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- First Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.firstName') }}</label>
            <input 
              type="text" 
              v-model="editingCustomer.first_name" 
              required
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- Last Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.lastName') }}</label>
            <input 
              type="text" 
              v-model="editingCustomer.last_name" 
              required
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- Email -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.email') }}</label>
            <input 
              type="email" 
              v-model="editingCustomer.email" 
              required
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- Phone -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.phone') }}</label>
            <input 
              type="tel" 
              v-model="editingCustomer.phone" 
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- Address -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.address') }}</label>
            <input 
              type="text" 
              v-model="editingCustomer.address" 
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- City -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.city') }}</label>
            <input 
              type="text" 
              v-model="editingCustomer.city" 
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- State -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.state') }}</label>
            <input 
              type="text" 
              v-model="editingCustomer.state" 
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- Country -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.country') }}</label>
            <input 
              type="text" 
              v-model="editingCustomer.country" 
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            />
          </div>
          
          <!-- Received Amount -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.receivedAmount') }}</label>
            <div class="relative">
              <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₪</span>
              <input 
                type="number" 
                min="0"
                step="0.01"
                v-model="editingCustomer.received" 
                placeholder="0.00"
                class="bg-gray-50 border border-gray-200 rounded-md w-full pl-8 pr-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
              />
            </div>
          </div>
          
          <!-- Status -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('crm.customers.status') }}</label>
            <select 
              v-model="editingCustomer.status" 
              required
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
            >
              <option v-for="status in statusOptions" :key="status" :value="status">
                {{ status === 'All' ? directT('crm.customers.all') : 
                   status === 'New' ? directT('crm.customers.new') : 
                   status === 'Active' ? directT('crm.customers.active') : 
                   status === 'Inactive' ? directT('crm.customers.inactive') : status }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="flex flex-col sm:flex-row sm:justify-end gap-2">
          <button 
            type="button" 
            @click="cancelEdit"
            class="w-full sm:w-auto px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746] order-2 sm:order-1"
          >
            {{ directT('crm.customers.cancel') }}
          </button>
          <button 
            type="submit" 
            class="w-full sm:w-auto px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all order-1 sm:order-2 mb-2 sm:mb-0"
            :disabled="saving"
          >
            {{ saving ? directT('crm.customers.saving') : directT('crm.customers.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import Pagination from "@/components/Common/Pagination.vue";
import { createToast } from "@/utils/toast";
import { useRouter } from "vue-router";
import stateStore from "@/utils/store";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// Interface for custom fields
interface CustomField {
  id?: string;
  field: string;
  isrequired: boolean;
  organizationid?: string;
}

export default defineComponent({
  name: "CustomersList",
  components: {
    Pagination,
  },
  setup() {
    const router = useRouter();
    const clients = ref<any[]>([]);
    const loading = ref(true);
    const isActive = ref(false);
    const searchQuery = ref("");
    const statusFilter = ref("All");
    const statusOptions = ref(["All", "New", "Active", "Inactive"]);
    const organizationId = ref<string | null>(null);
    const selectedClients = ref<string[]>([]);
    const selectAll = ref(false);
    
    // New Customer Modal
    const showAddModal = ref(false);
    const saving = ref(false);
    const newCustomer = ref({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      status: 'New',
      received: 0,
      organization_id: ''
    });

    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleting = ref(false);
    const clientToDelete = ref<string | null>(null);

    // View Customer modal
    const showViewModal = ref(false);
    const selectedCustomer = ref<any | null>(null);
    
    // Edit Customer modal
    const showEditModal = ref(false);
    const editingCustomer = ref<any>({});

    // הוספת משתנים לשדות מותאמים אישית
    const customFields = ref<CustomField[]>([]);
    const loadingCustomFields = ref(false);
    const customFieldValues = ref<Record<string, string>>({});

    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Toggle dropdown
    const toggleActive = () => {
      isActive.value = !isActive.value;
    };

    // Set status filter
    const setStatusFilter = (status: string) => {
      statusFilter.value = status;
      isActive.value = false;
    };

    // Format date for display
    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    };

    // Format currency for display
    const formatCurrency = (amount: number | null) => {
      if (amount === null || amount === undefined) return '₪0.00';
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 2
      }).format(amount);
    };

    // Filter clients based on search query and status
    const filteredClients = computed(() => {
      let result = clients.value;
      
      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(client => 
          (client.first_name && client.first_name.toLowerCase().includes(query)) ||
          (client.last_name && client.last_name.toLowerCase().includes(query)) ||
          (client.email && client.email.toLowerCase().includes(query)) ||
          (client.phone && client.phone.includes(query))
        );
      }
      
      // Filter by status
      if (statusFilter.value !== 'All') {
        result = result.filter(client => 
          client.status && client.status.toLowerCase() === statusFilter.value.toLowerCase()
        );
      }
      
      return result;
    });

    // Select or deselect all clients
    const selectAllClients = () => {
      if (selectAll.value) {
        selectedClients.value = filteredClients.value.map(client => client.id);
      } else {
        selectedClients.value = [];
      }
    };

    // Delete a client - Step 1: Open confirmation modal
    const deleteClient = (id: string) => {
      clientToDelete.value = id;
      showDeleteModal.value = true;
    };
    
    // Cancel delete operation
    const cancelDelete = () => {
      showDeleteModal.value = false;
      clientToDelete.value = null;
    };
    
    // Confirm and execute delete - Step 2: Execute the deletion
    const confirmDelete = async () => {
      if (!clientToDelete.value) return;
      
      deleting.value = true;
      
      try {
        const { error } = await supabase
          .from('clients')
          .delete()
          .eq('id', clientToDelete.value);
          
        if (error) throw error;
        
        // Remove from local array
        clients.value = clients.value.filter(client => client.id !== clientToDelete.value);
        createToast('Customer deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting client:', error);
        createToast('Failed to delete client. Please try again.', 'error');
      } finally {
        deleting.value = false;
        showDeleteModal.value = false;
        clientToDelete.value = null;
      }
    };

    // Add a new customer - open modal
    const addNewCustomer = () => {
      // Reset form
      newCustomer.value = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: '',
        status: 'New',
        received: 0,
        organization_id: organizationId.value || ''
      };
      showAddModal.value = true;
    };
    
    // Close the modal
    const closeModal = () => {
      showAddModal.value = false;
    };
    
    // Save customer to database
    const saveCustomer = async () => {
      if (!organizationId.value) {
        createToast('Cannot save customer: Missing organization ID', 'error');
        return;
      }
      
      saving.value = true;
      
      try {
        newCustomer.value.organization_id = organizationId.value;
        
        // הוספת לקוח חדש
        const { data, error } = await supabase
          .from('clients')
          .insert([newCustomer.value])
          .select();
          
        if (error) throw error;
        
        if (data && data[0]) {
          const customerId = data[0].id;
          
          // שמירת שדות מותאמים אישית
          const requiredCustomFields = customFields.value.filter(field => field.isrequired);
          if (requiredCustomFields.length > 0) {
            // הכנת רשומות לשמירה
            const customFieldsToSave = [];
            
            for (const field of requiredCustomFields) {
              if (customFieldValues.value[field.field]) {
                customFieldsToSave.push({
                  customerid: customerId,
                  customerscustomfiledid: field.id,
                  fieldcontent: customFieldValues.value[field.field]
                });
              }
            }
            
            // שמירת שדות מותאמים אישית אם יש
            if (customFieldsToSave.length > 0) {
              const { error: customFieldsError } = await supabase
                .from('customercustomfieldscontent')
                .insert(customFieldsToSave);
                
              if (customFieldsError) {
                console.error('Error saving custom fields:', customFieldsError);
                // לא נזרוק שגיאה כדי שהלקוח עדיין יישמר
              }
            }
          }
          
          // הוספת הלקוח החדש לרשימה המקומית
          clients.value.push(data[0]);
          createToast('Customer added successfully', 'success');
          closeModal();
          
          // איפוס ערכי שדות מותאמים אישית
          Object.keys(customFieldValues.value).forEach(key => {
            customFieldValues.value[key] = '';
          });
        }
      } catch (error) {
        console.error('Error saving customer:', error);
        createToast('Failed to save customer', 'error');
      } finally {
        saving.value = false;
      }
    };

    // Fetch clients from Supabase
    const fetchClients = async () => {
      try {
        // Get current organization_id
        organizationId.value = await getCurrentOrgId();
        
        // If organizationId is still missing, try to get it from user_profiles
        if (!organizationId.value) {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: userProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', user.id)
              .single();
            
            if (profileError) {
              console.error('Error fetching user profile:', profileError);
            } else if (userProfile && userProfile.org_id) {
              organizationId.value = userProfile.org_id as string;
              console.log('Found organization ID from user profile:', organizationId.value);
            }
          }
        }
        
        if (!organizationId.value) {
          loading.value = false;
          createToast('Cannot load clients: User is not associated with an organization', 'error');
          return;
        }
        
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('organization_id', organizationId.value);
          
        if (error) throw error;
        
        
        clients.value = data || [];
      } catch (error) {
        console.error('Error fetching clients:', error);
        createToast('Failed to load clients. Please try again.', 'error');
      } finally {
        loading.value = false;
      }
    };

    // פונקציה לטעינת שדות מותאמים אישית
    const fetchCustomFields = async () => {
      try {
        // If organization ID is missing, try to get it from user_profiles
        if (!organizationId.value) {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: userProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', user.id)
              .single();
            
            if (profileError) {
              console.error('Error fetching user profile:', profileError);
              throw profileError;
            }
            
            if (userProfile && userProfile.org_id) {
              organizationId.value = userProfile.org_id as string;
              console.log('Found organization ID from user profile:', organizationId.value);
            } else {
              console.warn('No organization ID found in user profile');
              loadingCustomFields.value = false;
              return;
            }
          } else {
            console.warn('Cannot fetch custom fields: User is not logged in');
            loadingCustomFields.value = false;
            return;
          }
        }
        
        loadingCustomFields.value = true;
        
        const { data, error } = await supabase
          .from('customerscustomfields')
          .select('*')
          .eq('organizationid', organizationId.value);
        
        if (error) throw error;
        
        // מיפוי הנתונים למבנה הנדרש
        customFields.value = (data || []).map((item: any) => ({
          id: item.id,
          field: item.field,
          isrequired: item.isrequired,
          organizationid: item.organizationid
        }));
        
        // איפוס ערכי השדות
        customFields.value.forEach(field => {
          customFieldValues.value[field.field] = '';
        });
        
        console.log('Custom fields loaded:', customFields.value);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        createToast('Failed to load custom fields', 'error');
      } finally {
        loadingCustomFields.value = false;
      }
    };

    // Close dropdown when clicking outside
    const closeDropdown = (event: MouseEvent) => {
      if (isActive.value) {
        isActive.value = false;
      }
    };

    // Add event listener for closing dropdown
    onMounted(() => {
      document.addEventListener('click', closeDropdown);
      fetchClients();
      fetchCustomFields(); // טעינת שדות מותאמים אישית
    });

    // Watch for changes in selection
    watch(selectedClients, () => {
      selectAll.value = selectedClients.value.length === filteredClients.value.length && filteredClients.value.length > 0;
    });

    // View customer
    const viewCustomer = (client: any) => {
      router.push(`/crm/customers/${client.id}`);
    };

    // Close view modal
    const closeViewModal = () => {
      showViewModal.value = false;
    };

    // Edit customer
    const editCustomer = (client: any) => {
      router.push(`/crm/customers/${client.id}`);
    };
    
    // Cancel edit
    const cancelEdit = () => {
      showEditModal.value = false;
    };
    
    // Save edited customer
    const saveEdit = async () => {
      if (!editingCustomer.value.id) {
        createToast('Customer ID is required', 'error');
        return;
      }
      
      saving.value = true;
      
      try {
        const { data, error } = await supabase
          .from('clients')
          .update(editingCustomer.value)
          .eq('id', editingCustomer.value.id)
          .select();
          
        if (error) {
          console.error('Supabase error details:', error);
          throw error;
        }
        
        // Update client in the list
        if (data && data.length > 0) {
          const index = clients.value.findIndex(c => c.id === editingCustomer.value.id);
          if (index !== -1) {
            clients.value[index] = data[0];
          }
          createToast('Customer updated successfully!', 'success');
        }
        
        // Close modal
        showEditModal.value = false;
      } catch (error) {
        console.error('Error updating customer:', error);
        createToast(`Failed to update customer: ${error instanceof Error ? error.message : 'Access denied. Check permissions.'}`, 'error');
      } finally {
        saving.value = false;
      }
    };

    return {
      clients,
      loading,
      isActive,
      toggleActive,
      searchQuery,
      statusFilter,
      statusOptions,
      setStatusFilter,
      formatDate,
      formatCurrency,
      filteredClients,
      selectedClients,
      selectAll,
      selectAllClients,
      deleteClient,
      addNewCustomer,
      // New modal properties and methods
      showAddModal,
      closeModal,
      newCustomer,
      saveCustomer,
      saving,
      // Delete modal properties and methods
      showDeleteModal,
      cancelDelete,
      confirmDelete,
      deleting,
      // View modal properties and methods
      showViewModal,
      closeViewModal,
      selectedCustomer,
      viewCustomer,
      // Edit modal properties and methods
      showEditModal,
      editingCustomer,
      editCustomer,
      cancelEdit,
      saveEdit,
      // Custom fields
      customFields,
      loadingCustomFields,
      customFieldValues,
      directT
    };
  },
});
</script>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  animation: spin 1s ease infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
